import { z } from "zod";
import { canonicalUuidSchema, reportFilterStateV1Schema, } from "./report-filters.js";
import { videoSelectionSchema } from "./video.js";
export const ANALYTICS_MAX_BODY_BYTES = 16 * 1_024;
export const ANALYTICS_MAX_IDENTIFIER_LENGTH = 255;
export const ANALYTICS_MAX_COLLECTION_ITEMS = 100;
const uuidV7Schema = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/, "Expected a canonical UUIDv7");
const analyticsSensitiveTextPatterns = [
    /https?:\/\//iu,
    /\bauthorization\b["']?\s*[:=]/iu,
    /\bbearer\s+[A-Za-z0-9._~+/=-]{20,}/iu,
    /(?:^|[?&\s{,;])["']?(?:access[_-]?token|api[_-]?key|signature|token|password|client[\s_-]?secret|x-goog-signature)["']?\s*[:=]/iu,
    /\b(?:phx|phc)_[A-Za-z0-9_-]+/iu,
    /(?:^|[^A-Za-z0-9_-])eyJ[A-Za-z0-9_-]{2,}\.[A-Za-z0-9_-]{2,}\.[A-Za-z0-9_-]{2,}(?:$|[^A-Za-z0-9_-])/u,
    /-----BEGIN (?:[A-Z0-9]+ )*PRIVATE KEY-----/iu,
];
const containsAnalyticsSensitiveText = (value) => analyticsSensitiveTextPatterns.some((pattern) => pattern.test(value));
const safeText = (maximum) => z
    .string()
    .trim()
    .min(1)
    .max(maximum)
    .refine((value) => !containsAnalyticsSensitiveText(value), "Sensitive text is not allowed");
const analyticsFilterStateSchema = reportFilterStateV1Schema.refine(({ adNameSearch }) => adNameSearch === null || !containsAnalyticsSensitiveText(adNameSearch), { message: "Sensitive search text is not allowed", path: ["adNameSearch"] });
const identifier = safeText(ANALYTICS_MAX_IDENTIFIER_LENGTH);
const identifiers = (item = identifier) => z
    .array(item)
    .max(ANALYTICS_MAX_COLLECTION_ITEMS)
    .refine((values) => new Set(values).size === values.length, "Identifiers must be unique");
const analyticsVideoSelectionSchema = videoSelectionSchema.refine(({ videoId }) => !containsAnalyticsSensitiveText(videoId), { message: "Sensitive video identifier is not allowed", path: ["videoId"] });
const videoSelections = (minimum = 0) => z
    .array(analyticsVideoSelectionSchema)
    .min(minimum)
    .max(ANALYTICS_MAX_COLLECTION_ITEMS)
    .refine((values) => new Set(values.map(({ brandId, videoId }) => `${brandId}\u0000${videoId}`)).size
    === values.length, "Video selections must be unique")
    .readonly();
export const analyticsEnvironmentSchema = z.enum([
    "production",
    "staging",
    "development",
]);
export const analyticsChangedFieldSchema = z.enum([
    "accountIds",
    "adNameSearch",
    "all",
    "brandIds",
    "date",
    "durationMax",
    "durationMin",
    "firstSeenFrom",
    "firstSeenTo",
    "groupSelections",
    "objectives",
    "optimizationGoals",
    "playStatuses",
    "roasMax",
    "roasMin",
    "spendMax",
    "spendMin",
]);
const changedFieldsSchema = z
    .array(analyticsChangedFieldSchema)
    .min(1)
    .max(analyticsChangedFieldSchema.options.length)
    .refine((values) => new Set(values).size === values.length, "Changed fields must be unique");
export const analyticsScreenSchema = z.enum([
    "report",
    "video-detail",
    "comparison",
    "saved-views",
    "admin",
    "admin-users",
    "admin-subgroups",
    "admin-brands",
    "admin-domains",
]);
const analyticsPathSchema = z.enum([
    "/",
    "/saved-views",
    "/admin",
    "/admin/users",
    "/admin/subgroups",
    "/admin/brands",
    "/admin/domains",
]);
const commonEnvelope = {
    sessionId: uuidV7Schema,
    verificationRunId: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/)
        .optional(),
};
function captureSchema(event, properties) {
    return z
        .object({
        ...commonEnvelope,
        event: z.literal(event),
        properties,
    })
        .strict();
}
const pageviewSchema = captureSchema("$pageview", z.object({ path: analyticsPathSchema, screen: analyticsScreenSchema }).strict());
const searchSubmittedSchema = captureSchema("search submitted", z
    .object({
    changedFields: changedFieldsSchema,
    filters: analyticsFilterStateSchema,
    query: safeText(256),
})
    .strict());
const filtersAppliedSchema = captureSchema("filters applied", z
    .object({
    changedFields: changedFieldsSchema,
    filters: analyticsFilterStateSchema,
})
    .strict());
const filtersResetSchema = captureSchema("filters reset", z
    .object({
    changedFields: changedFieldsSchema,
    filters: analyticsFilterStateSchema,
})
    .strict());
const playbackStartedSchema = captureSchema("video playback started", z
    .object({
    brandId: canonicalUuidSchema,
    surface: z.enum(["report", "detail", "comparison"]),
    videoId: identifier,
})
    .strict());
const resultsPageChangedSchema = captureSchema("results page changed", z
    .object({
    direction: z.enum(["next", "previous"]),
    fromPage: z.number().int().min(1).max(1_000_000),
    pageSize: z.number().int().min(1).max(1_000),
    toPage: z.number().int().min(1).max(1_000_000),
})
    .strict()
    .superRefine(({ direction, fromPage, toPage }, context) => {
    const valid = direction === "next" ? toPage === fromPage + 1 : toPage === fromPage - 1;
    if (!valid)
        context.addIssue({ code: "custom", message: "Page transition is invalid" });
}));
const comparisonSelectionChangedSchema = captureSchema("comparison selection changed", z
    .object({
    action: z.enum(["add", "remove", "clear"]),
    selectedCount: z.number().int().min(0).max(ANALYTICS_MAX_COLLECTION_ITEMS),
    videos: videoSelections(),
})
    .strict()
    .refine(({ selectedCount, videos }) => selectedCount === videos.length, "Selected count must match video selections"));
const comparisonViewedSchema = captureSchema("comparison viewed", z
    .object({
    selectedCount: z.number().int().min(2).max(ANALYTICS_MAX_COLLECTION_ITEMS),
    videos: videoSelections(2),
})
    .strict()
    .refine(({ selectedCount, videos }) => selectedCount === videos.length, "Selected count must match video selections"));
const reportInteractionSchema = captureSchema("report interaction completed", z
    .object({
    action: z.enum(["changed", "opened", "copied"]),
    feature: z.enum(["sort", "column", "detail", "trend", "share"]),
    state: z.enum(["enabled", "disabled", "ascending", "descending"]).optional(),
    surface: z.enum(["report", "detail", "comparison"]),
})
    .strict());
const savedViewActionSchema = captureSchema("saved view action completed", z
    .object({
    action: z.enum([
        "create",
        "update",
        "open",
        "delete",
        "rename",
        "set-default",
        "clear-default",
        "add-collaborator",
        "remove-collaborator",
    ]),
    count: z.number().int().min(0).max(10_000).optional(),
    status: z.literal("success"),
})
    .strict());
const adminActionSchema = captureSchema("admin action completed", z
    .object({
    action: z.enum([
        "create",
        "update",
        "delete",
        "enable",
        "disable",
        "grant",
        "revoke",
        "map",
        "unmap",
        "reorder",
        "run-diagnostic",
    ]),
    brandId: canonicalUuidSchema.optional(),
    count: z.number().int().min(0).max(10_000).optional(),
    entity: z.enum([
        "user",
        "grant",
        "subgroup",
        "subgroup-value",
        "brand",
        "mapping",
        "domain",
        "diagnostic",
    ]),
    status: z.literal("success"),
})
    .strict());
export const analyticsCaptureRequestSchema = z
    .discriminatedUnion("event", [
    pageviewSchema,
    searchSubmittedSchema,
    filtersAppliedSchema,
    filtersResetSchema,
    playbackStartedSchema,
    resultsPageChangedSchema,
    comparisonSelectionChangedSchema,
    comparisonViewedSchema,
    reportInteractionSchema,
    savedViewActionSchema,
    adminActionSchema,
])
    .superRefine((request, context) => {
    const bytes = new TextEncoder().encode(JSON.stringify(request)).byteLength;
    if (bytes > ANALYTICS_MAX_BODY_BYTES) {
        context.addIssue({ code: "custom", message: "Analytics request is too large" });
    }
});
//# sourceMappingURL=analytics.js.map